import * as icons from "@/components/icons";
import { IGlobalSettings, IMessage, IRequest, IRequestResponse, IUser, Priority, Role } from "@/models";
import Vue from "vue";
import { Component } from "vue-property-decorator";

@Component({
    name: "dashboard",
    components: {
        "warning-icon": icons.WarningIcon,
        "search-loop-icon": icons.SearchLoopIcon,
    },
})
export default class Dashboard extends Vue {
    public promises: Array<Promise<any>>;

    constructor() {
        super();
        this.promises = [];

        if (!this.$store.state.app.user) {
            this.promises.push(this.$store.dispatch("app/getUser"));
        }

        const query: Array<[string, string]> = [];

        this.promises.push(this.$store.dispatch("request/fetchUserRequests", query));
        this.promises.push(this.$store.dispatch("request/fetchGlobalSettings"));
    }

    public get user(): IUser {
        return this.$store.state.app.user;
    }

    public get Priority(): typeof Priority {
        return Priority;
    }

    public get userRequests(): IRequestResponse {
        return this.$store.state.request.userRequests;
    }

    public get userRequestsToDisplay(): IRequest[] {
        if (!!this.userRequests?.requests) {
            if (this.isVip || this.isAdmin) {
                return this.requestFilteredByPriority.slice(0, 12);
            } else {
                return this.userRequests.requests.filter(r => r.requestStatus.step < 2).slice(0, 12);
            }
        } else {
            return [];
        }
    }

    public get hasRequestsForRole(): boolean {
        return (this.userRequests?.totalVolume ?? 0) > 0;
    }

    public get vipOrAdminRequestsToDisplay(): IRequest[] {
        switch (this.user.role) {
            case Role.Vip:
                return this.userRequests.requests
                    .filter(r => r.requestStatus.step > 0 && r.requestStatus.step < 2)
                    // Do not display request if VIP commented it.
                    .filter(r => !r.messages.some(m => m.createdBy.userInfoId === this.user.userInfoId))
                    // Do not display request if VIP reacted to it.
                    .filter(r => !r.messages.some(m => m.reactions.some(react => react.createdBy.userInfoId === this.user.userInfoId)));

            default:
                return this.userRequests.requests
                    .filter(r => r.requestStatus.step > 0 && r.requestStatus.step < 2);
        }
    }

    public get requestFilteredByPriority(): IRequest[] {
        let orderedRequests: IRequest[] = [];

        const vipOrAdminRequestsToDisplayWithUpdatedPriority = this.assignPriorityBasedOnDeadline(this.vipOrAdminRequestsToDisplay);

        const requestsWithoutPriorities = this.sortArray(vipOrAdminRequestsToDisplayWithUpdatedPriority?.filter(r => !r.priority));
        const requestsCritical = this.sortArray(vipOrAdminRequestsToDisplayWithUpdatedPriority?.filter(r => r.priority === Priority.Critical));
        const requestsUrgent = this.sortArray(vipOrAdminRequestsToDisplayWithUpdatedPriority?.filter(r => r.priority === Priority.Urgent));

        orderedRequests = orderedRequests.concat(requestsCritical).concat(requestsUrgent).concat(requestsWithoutPriorities);

        return orderedRequests;
    }

    public get globalSettings(): IGlobalSettings {
        return this.$store.getters["request/getGlobalSettings"];
    }

    public get globalMessageVisible(): boolean {
        return this.globalSettings?.globalMessage ? this.globalSettings.globalMessage.length > 0 : false;
    }

    public get isAdmin(): boolean {
        return this.user?.role === Role.Admin;
    }

    public get isVip(): boolean {
        return this.user?.role === Role.Vip;
    }

    public get isUser(): boolean {
        return this.user?.role === Role.User;
    }

    public assignPriorityBasedOnDeadline(requests: IRequest[]): IRequest[] {
        return requests.map(r => {
            if (r.priority != Priority.Critical || r.priority === null) {
                const dayBeforeDeadline = this.convertMsToDay(this.msBeforeDeadline(r));

                if (dayBeforeDeadline <= 2) {
                    return { ...r, priority: Priority.Critical };
                }
                if (dayBeforeDeadline <= 4 && dayBeforeDeadline > 2) {
                    return { ...r, priority: Priority.Urgent };
                }
            }
            return r;
        });
    }

    public convertMsToDay(msValue: number): number {
        return msValue / 86400000;
    }

    public decisionDeadlineToDisplay(deadline: Date): string {
        if (deadline) {
            return deadline.toString().slice(0, 10);
        } else {
            return " ";
        }
    }

    public getIssueCount(messages: IMessage[]): [number, number] {
        let yesCount = 0;
        let noCount = 0;

        messages.forEach(m => {
            m.issue ? yesCount++ : noCount++;
        });

        return [yesCount, noCount];
    }

    public getRequestAdvice(request: IRequest): string {
        return !!request?.advice ? "advice" : "request";
    }

    public getRequestStatusClass(request: IRequest): string {
        return this.$options.filters
            .displayedStatus(request?.requestStatus, this.user?.role)
            .split(" ")
            .join("-")
            .toLowerCase();
    }

    public getTabletColor(priority: Priority): string {
        switch (priority) {
            case Priority.Critical:
                return "tablet red";

            case Priority.Urgent:
                return "tablet orange";

            default:
                return "tablet"
        }
    }

    public msBeforeDeadline(request: IRequest): number {
        const todayISO = new Date().toISOString();
        const today: Date = new Date(todayISO);

        if (request && request?.decisionDeadline) {
            const deadline: Date = new Date(request.decisionDeadline);
            return deadline.getTime() - today.getTime();
        } else {
            return 0; // in case of nullable deadline (even if it's theorically impossible)
        }
    }

    public sortArray(arrayToSort: IRequest[]): IRequest[] {
        if (arrayToSort) {
            return arrayToSort.sort((a, b) => this.msBeforeDeadline(a) - this.msBeforeDeadline(b));
        } else {
            return arrayToSort;
        }
    }
}
